import {Component, type ErrorInfo, type ReactNode} from 'react';
import {Alert, Box, Button, Stack, Typography} from '@mui/material';

interface Props {
   children: ReactNode;
   /**
    * Navigates to a known-good, already-loaded route (Wells list) and
    * clears the caught error in one go — see handleReset below for why
    * both have to happen together. Supplied by AppRoutes.tsx, which has
    * useNavigate() available (this class component doesn't).
    */
   onReset: () => void;
}

interface State {
   error: Error | null;
}

// Vite/browsers phrase a failed dynamic import() of a route chunk a few
// different ways ("Failed to fetch dynamically imported module",
// "error loading dynamically imported module", Firefox's "error loading
// module script") — matched loosely so any of them is treated as a
// chunk-load failure rather than a generic page crash below.
const CHUNK_LOAD_ERROR = /dynamically imported module|loading chunk|module script/i;

/**
 * Catches errors thrown while rendering a route — most importantly the
 * ones React.lazy() throws when a route's JS chunk can't be fetched
 * (see routes/AppRoutes.tsx's route-level code-splitting comment, added
 * 2026-08-31). A field User tapping from Wells (already loaded) into a
 * page whose chunk this browser tab never downloaded — Daily Report,
 * say — while offline hits exactly this: the dynamic import has to go
 * to the network, the network isn't there, and with nothing catching
 * it React unmounts the ENTIRE app with no boundary above it. Blank
 * white screen, no error, no way back short of a manual reload.
 * useRoutePrefetch.ts (warms these chunks ahead of time while online)
 * is the actual fix for the common case; this is the safety net for
 * whatever it doesn't cover (e.g. the device was offline from the very
 * first page load, before prefetching ever got a chance to run).
 *
 * IMPORTANT: React.lazy() calls its loader exactly once per page load
 * and caches whatever it gets — success OR failure — forever after.
 * Once a chunk fetch has failed, re-rendering that same lazy()
 * component throws the identical cached rejection again, instantly,
 * every time, even after connectivity returns — there is no working
 * in-app retry for THAT specific failure. handleReset below never just
 * clears local state for a chunk-load error; it also navigates (via
 * onReset) to Wells, a DIFFERENT route whose own lazy() already
 * resolved successfully the first time the app loaded, so no re-import
 * is needed. A non-chunk error (a genuine render bug) has no such
 * cache to fight, so the same reset still gives it a clean retry.
 */
export class RouteErrorBoundary extends Component<Props, State> {
   state: State = {error: null};

   static getDerivedStateFromError(error: Error): State {
      return {error};
   }

   componentDidCatch(error: Error, info: ErrorInfo) {
      console.error('Route render failed:', error, info.componentStack);
   }

   handleReset = () => {
      this.props.onReset();
      this.setState({error: null});
   };

   render() {
      const {error} = this.state;
      if (!error) return this.props.children;

      const isChunkLoadError = CHUNK_LOAD_ERROR.test(error.message);
      // Best-effort only, same as everywhere else offline UX is decided
      // off navigator.onLine directly (this is a class component — the
      // app's own probed isOnlineAtom isn't reachable via hooks here) —
      // just for choosing which message to show, not for any logic.
      const offline = !navigator.onLine;

      return (
         <Box
            sx={{
               display: 'flex',
               flexDirection: 'column',
               alignItems: 'center',
               justifyContent: 'center',
               height: '100vh',
               px: 3,
               textAlign: 'center',
               gap: 2,
            }}
         >
            <Alert severity={isChunkLoadError && offline ? 'info' : 'error'} sx={{maxWidth: 420}}>
               {isChunkLoadError
                  ? offline
                     ? "This page hasn't been downloaded to your device yet, and you're currently offline. Connect to the internet once so it can load — after that it'll keep working offline."
                     : "This page couldn't load. A new version of the app may be available."
                  : 'Something went wrong loading this page.'}
            </Alert>
            <Stack direction='row' spacing={1.5}>
               <Button variant='contained' onClick={this.handleReset}>
                  Back to Wells
               </Button>
            </Stack>
            {!isChunkLoadError && (
               <Typography variant='caption' color='text.secondary' sx={{maxWidth: 420}}>
                  {error.message}
               </Typography>
            )}
         </Box>
      );
   }
}
